import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Link,
    Card,
    CardContent,
    TablePagination,
    Skeleton,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as d3 from 'd3';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/zh-cn'; // Import Chinese locale for moment
// This import is a placeholder for your actual constants file.
import { API_BASE_URL } from '../../utils/constants';

// --- Constants ---
const SATOSHIS_IN_BTC = 1e11;

// --- Theme ---
const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#90caf9' },
        secondary: { main: '#f48fb1' },
        background: { default: '#121212', paper: '#1e1e1e' },
        success: { main: '#81c784' },
        error: { main: '#ff8a65' },
        text: { primary: '#e0e0e0', secondary: '#b0b0b0' },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h4: { fontWeight: 700 },
        h5: { fontWeight: 600 },
    },
});

// --- D3 Bar Chart Component ---
const TransactionChart = ({ data }) => {
    const ref = useRef();
    const { t } = useTranslation();
    const [dimensions, setDimensions] = useState({ width: 0, height: 350 });

    useEffect(() => {
        const handleResize = () => {
            if (ref.current) {
                setDimensions({
                    width: ref.current.clientWidth,
                    height: 350,
                });
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!data || !dimensions.width) {
            return;
        }

        const svg = d3.select(ref.current);
        svg.selectAll('*').remove(); // Clear previous render

        const allTransactions = [
            ...data.depositsFromAddress.transactions.map((t) => ({ ...t, type: 'deposit' })),
            ...data.withdrawalsToAddress.transactions.map((t) => ({ ...t, type: 'withdrawal' })),
        ].sort((a, b) => new Date(a.time) - new Date(b.time));

        if (allTransactions.length === 0) {
            svg.append('text')
                .attr('x', dimensions.width / 2)
                .attr('y', dimensions.height / 2)
                .attr('text-anchor', 'middle')
                .style('fill', theme.palette.text.secondary)
                .text(t('addressPage.transactionChart.noData'));
            return;
        }

        const margin = { top: 50, right: 40, bottom: 80, left: 70 };
        const width = dimensions.width - margin.left - margin.right;
        const height = dimensions.height - margin.top - margin.bottom;
        const MIN_BAR_HEIGHT = 2;

        const x = d3
            .scaleBand()
            .domain(allTransactions.map((d) => d._id))
            .range([0, width])
            .padding(0.4);

        const yExtent = d3.extent(allTransactions, (d) => d.amount / SATOSHIS_IN_BTC);
        const yDomain = yExtent[0] !== undefined ? yExtent : [0, 1];

        const y = d3.scaleLinear().domain(yDomain).nice().range([height, 0]);

        const chart = svg.append('g').attr('transform', `translate(${ margin.left }, ${ margin.top })`);

        chart.append('g').attr('transform', `translate(0, ${ height })`).call(d3.axisBottom(x).tickSize(0).tickFormat(''));

        chart
            .append('g')
            .call(d3.axisLeft(y).ticks(5).tickFormat((d) => `${ d.toFixed(2) }`))
            .call((g) => g.select('.domain').remove())
            .call((g) => g.selectAll('.tick line').clone().attr('x2', width).attr('stroke-opacity', 0.1));

        svg.append('text')
            .attr('text-anchor', 'middle')
            .attr('transform', `translate(${ margin.left / 3 }, ${ margin.top + height / 2 }) rotate(-90)`)
            .style('fill', theme.palette.text.secondary)
            .text(t('addressPage.transactionChart.yAxisLabel'));

        svg.append('text')
            .attr('text-anchor', 'middle')
            .attr('transform', `translate(${ margin.left + width / 2 }, ${ height + margin.top + 50 })`)
            .style('fill', theme.palette.text.secondary)
            .text(t('addressPage.transactionChart.xAxisLabel'));

        const tooltip = d3
            .select('body')
            .append('div')
            .attr('class', 'd3-tooltip')
            .style('position', 'absolute')
            .style('z-index', '10')
            .style('visibility', 'hidden')
            .style('background', 'rgba(0,0,0,0.8)')
            .style('border-radius', '5px')
            .style('padding', '8px 12px')
            .style('color', 'white')
            .style('font-size', '12px')
            .style('pointer-events', 'none');

        chart
            .selectAll('.bar')
            .data(allTransactions)
            .enter()
            .append('rect')
            .attr('class', 'bar')
            .attr('fill', (d) => (d.type === 'deposit' ? theme.palette.success.main : theme.palette.error.main))
            .attr('x', (d) => x(d._id))
            .attr('width', x.bandwidth())
            .attr('height', (d) => {
                const yPos = y(d.amount / SATOSHIS_IN_BTC);
                const barHeight = height - yPos;
                return Math.max(barHeight, MIN_BAR_HEIGHT);
            })
            .attr('y', (d) => {
                const yPos = y(d.amount / SATOSHIS_IN_BTC);
                const barHeight = height - yPos;
                const finalHeight = Math.max(barHeight, MIN_BAR_HEIGHT);
                return height - finalHeight;
            })
            .on('mouseover', (event, d) => {
                d3.select(event.currentTarget).style('opacity', 0.8);
                tooltip
                    .style('visibility', 'visible')
                    .html(
                        `<strong>${ d.type === 'deposit' ? t('addressPage.transactionChart.legend.deposit') : t('addressPage.transactionChart.legend.withdrawal') }</strong><br/>
                           ${ t('addressPage.transactionChart.tooltip.amount') }: ${ (d.amount / SATOSHIS_IN_BTC).toFixed(8) } BTC<br/>
                           ${ t('addressPage.transactionChart.tooltip.date') }: ${ moment(d.time).format('L LT') }`
                    );
            })
            .on('mousemove', (event) => {
                tooltip.style('top', `${ event.pageY - 10 }px`).style('left', `${ event.pageX + 10 }px`);
            })
            .on('mouseout', (event) => {
                d3.select(event.currentTarget).style('opacity', 1);
                tooltip.style('visibility', 'hidden');
            });

        const legendData = [
            { label: t('addressPage.transactionChart.legend.deposit'), color: theme.palette.success.main },
            { label: t('addressPage.transactionChart.legend.withdrawal'), color: theme.palette.error.main },
        ];
        const legend = svg.append('g').attr('transform', `translate(${ margin.left }, 20)`);

        legendData.forEach((item, index) => {
            const legendItem = legend.append('g').attr('transform', `translate(${ index * 120 }, 0)`);
            legendItem.append('rect').attr('width', 12).attr('height', 12).attr('fill', item.color);
            legendItem
                .append('text')
                .attr('x', 18)
                .attr('y', 10)
                .text(item.label)
                .style('fill', theme.palette.text.primary)
                .style('font-size', '12px');
        });

        return () => tooltip.remove();
    }, [data, dimensions, t]);

    return (
        <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
                {t('addressPage.transactionChart.title')}
            </Typography>
            <Box sx={{ pr: 2 }}>
                <svg ref={ref} width="100%" height={dimensions.height}></svg>
            </Box>
        </Paper>
    );
};

// --- Reusable Transaction Table Component with Pagination ---
const TransactionTable = ({ title, transactions, type }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedTransactions = transactions.transactions.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <Typography variant="h5" gutterBottom component="div" sx={{ mb: 2 }}>
                {title} ({transactions.count})
            </Typography>
            <TableContainer>
                <Table stickyHeader aria-label={`${ type } table`}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('addressPage.transactionTable.header.txId')}</TableCell>
                            <TableCell>{t('addressPage.transactionTable.header.user')}</TableCell>
                            <TableCell align="right">{t('addressPage.transactionTable.header.amount')}</TableCell>
                            <TableCell>{t('addressPage.transactionTable.header.time')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedTransactions.length > 0 ? (
                            paginatedTransactions.map((tx) => (
                                <TableRow key={tx._id} hover>
                                    <TableCell>
                                        <Link component={RouterLink} to={`/explorer/transaction/${ tx.txId }`} underline="hover" color="primary" title={tx.txId}>
                                            {`${ tx.txId.substring(0, 6) }...${ tx.txId.substring(tx.txId.length - 6) }`}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <Link component={RouterLink} to={`/explorer/user/${ tx.user }`} underline="hover" color="primary">
                                            {tx.user}
                                        </Link>
                                    </TableCell>
                                    <TableCell align="right">{(tx.amount / SATOSHIS_IN_BTC).toFixed(8)}</TableCell>
                                    <TableCell>{moment(tx.time).format('L LT')}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    {type === 'deposits'
                                        ? t('addressPage.transactionTable.noDeposits')
                                        : t('addressPage.transactionTable.noWithdrawals')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={transactions.transactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
};

// --- Stat Card Component ---
const StatCard = ({ title, value, color }) => (
    <Card sx={{ backgroundColor: theme.palette.background.paper, height: '100%' }}>
        <CardContent>
            <Typography color="text.secondary" gutterBottom>
                {title}
            </Typography>
            <Typography variant="h5" component="div" sx={{ color: color, fontWeight: 'bold' }}>
                {value}
            </Typography>
        </CardContent>
    </Card>
);

// --- Loading Skeleton Component ---
const AddressPageSkeleton = () => (
    <>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            <Skeleton width="60%" />
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
            {[...Array(3)].map((_, index) => (
                <Grid item xs={12} sm={4} key={index}>
                    <Skeleton variant="rectangular" height={100} />
                </Grid>
            ))}
        </Grid>

        <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
                <Skeleton width="40%" />
            </Typography>
            <Skeleton variant="rectangular" height={350} />
        </Paper>

        <Grid container spacing={4}>
            {[...Array(2)].map((_, index) => (
                <Grid item xs={12} lg={6} key={index}>
                    <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
                        <Typography variant="h5" gutterBottom>
                            <Skeleton width="50%" />
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><Skeleton /></TableCell>
                                        <TableCell><Skeleton /></TableCell>
                                        <TableCell><Skeleton /></TableCell>
                                        <TableCell><Skeleton /></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {[...Array(5)].map((_, i) => (
                                        <TableRow key={i}>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton /></TableCell>
                                            <TableCell><Skeleton /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            ))}
        </Grid>
    </>
);


// --- Main Address Page Component ---
export default function AddressPage() {
    const { address } = useParams();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true); // Start with loading true
    const [error, setError] = useState('');

    // Sync moment locale with i18next language
    useEffect(() => {
        moment.locale(i18n.language === 'zh' ? 'zh-cn' : 'en');
    }, [i18n.language]);

    useEffect(() => {
        const fetchData = async (addr) => {
            if (!addr) {
                setLoading(false);
                return;
            };
            setLoading(true);
            setError('');
            // Don't set data to null here to avoid flicker on re-fetch

            try {
                const response = await fetch(`${ API_BASE_URL }/explorer/explore/address/${ addr }`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${ response.status }`);
                }
                const result = await response.json();
                setData(result);
            } catch (e) {
                console.error('Failed to fetch address data:', e);
                setError(t('addressPage.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchData(address);
    }, [address, t]);

    const stats = React.useMemo(() => {
        if (!data) return { totalIn: 0, totalOut: 0, netFlow: 0 };
        const totalIn = data.depositsFromAddress.transactions.reduce((sum, tx) => sum + tx.amount, 0);
        const totalOut = data.withdrawalsToAddress.transactions.reduce((sum, tx) => sum + tx.amount, 0);
        return {
            totalIn: totalIn / SATOSHIS_IN_BTC,
            totalOut: totalOut / SATOSHIS_IN_BTC,
            netFlow: (totalIn - totalOut) / SATOSHIS_IN_BTC,
        };
    }, [data]);

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" gutterBottom component="h1">
                    {t('addressPage.title')}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ my: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <AddressPageSkeleton />
                ) : (
                    data && !error && (
                        <>
                            <Typography variant="h6" gutterBottom sx={{ overflowWrap: 'break-word', mb: 3 }}>
                                {t('addressPage.detailsFor')}{' '}
                                <Typography component="span" color="primary">
                                    {data.address}
                                </Typography>
                            </Typography>

                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={12} sm={4}>
                                    <StatCard title={t('addressPage.statCard.totalIn')} value={stats.totalIn.toFixed(8)} color={theme.palette.success.main} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatCard title={t('addressPage.statCard.totalOut')} value={stats.totalOut.toFixed(8)} color={theme.palette.error.main} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatCard
                                        title={t('addressPage.statCard.netFlow')}
                                        value={stats.netFlow.toFixed(8)}
                                        color={stats.netFlow >= 0 ? theme.palette.success.main : theme.palette.error.main}
                                    />
                                </Grid>
                            </Grid>

                            <TransactionChart data={data} />

                            <Grid container spacing={4}>
                                <Grid item xs={12} lg={6}>
                                    <TransactionTable
                                        title={t('addressPage.depositsTitle')}
                                        transactions={data.depositsFromAddress}
                                        type="deposits"
                                    />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <TransactionTable
                                        title={t('addressPage.withdrawalsTitle')}
                                        transactions={data.withdrawalsToAddress}
                                        type="withdrawals"
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )
                )}
            </Container>
        </ThemeProvider>
    );
}
